<template>
  <div v-if="hasData" class="command-log-card">
    <div class="command-log-header">
      <!-- MOD: Cambié me-2 -> mr-2 para Bootstrap v4 -->
      <feather-icon icon="TerminalIcon" size="16" class="mr-2" />
      <span class="command-name">{{
        item.data.command_name || $t("alarm.timeline.unknown_command")
      }}</span>
    </div>

    <div class="command-log-body">
      <!-- =================== Resumen acción/target =================== -->
      <!-- MOD: Dejé de llamar parse varias veces. Uso computed actionAndTarget -->
      <div class="command-detail-item">
        <span class="command-detail-label">{{ $t("alarm.timeline.action") }}</span>
        <b-badge
          :variant="getActionBadgeVariant(actionAndTarget.action)"
          class="command-detail-value action-badge"
        >
          {{ actionAndTarget.action }}
        </b-badge>
      </div>

      <div class="command-detail-item">
        <span class="command-detail-label">{{ $t("alarm.timeline.target") }}</span>
        <span class="command-detail-value target-value">
          <code>{{ actionAndTarget.target }}</code>
        </span>
      </div>

      <!-- =================== Datos enriquecidos del comando =================== -->
      <div v-if="item.data.data" class="command-enhanced-info">
        <!-- Tipo de comando -->
        <div v-if="item.data.data.type" class="command-detail-item">
          <span class="command-detail-label">{{
            $t("alarm.timeline.command_type")
          }}</span>
          <b-badge variant="secondary" class="command-detail-value">
            {{ item.data.data.type }}
          </b-badge>
        </div>

        <!-- Nombre corto del comando -->
        <div v-if="item.data.data.cmd" class="command-detail-item">
          <span class="command-detail-label">{{ $t("alarm.timeline.command") }}</span>
          <span class="command-detail-value target-value">
            <code>{{ item.data.data.cmd }}</code>
          </span>
        </div>

        <!-- Etiqueta del comando -->
        <div v-if="item.data.data.cmd_label" class="command-detail-item">
          <span class="command-detail-label">{{
            $t("alarm.timeline.command_label")
          }}</span>
          <span class="command-detail-value">{{ item.data.data.cmd_label }}</span>
        </div>

        <!-- =================== cmd_data =================== -->
        <div v-if="item.data.data.cmd_data" class="command-data-section">
          <div class="command-detail-label mb-1">
            {{ $t("alarm.timeline.command_data") }}:
          </div>

          <!-- MOD: Caso AI Decision (alarm-image-inference / ai_decision) -->
          <div v-if="cmdData.ai_decision" class="ai-decision-data">
            <div class="command-detail-item">
              <span class="command-detail-label">{{
                $t("alarm.timeline.ai_prompt")
              }}</span>
              <span class="command-detail-value">
                <pre class="code-block">{{ cmdData.ai_decision.prompt }}</pre>
              </span>
            </div>

            <div class="command-detail-item">
              <span class="command-detail-label">{{
                $t("alarm.timeline.ai_decision")
              }}</span>
              <b-badge
                :style="{ backgroundColor: getAiDecisionColor(cmdData.ai_decision) }"
                class="command-detail-value"
              >
                {{ cmdData.ai_decision.ai_decision }}
              </b-badge>
            </div>

            <div v-if="cmdData.ai_decision.image_url" class="command-detail-item">
              <span class="command-detail-label">{{ $t("alarm.timeline.image") }}</span>
              <img
                :src="cmdData.ai_decision.image_url"
                alt="AI Decision Image"
                class="ai-decision-image"
                @click="openImageModal(cmdData.ai_decision.image_url)"
                v-b-modal.image_modal
              />
            </div>
          </div>

          <!-- MOD: Caso update:alarm_level -->
          <div
            v-else-if="typeof cmdData.alarm_levels !== 'undefined'"
            class="command-detail-item"
          >
            <span class="command-detail-label">{{
              $t("alarm.timeline.alarm_level")
            }}</span>
            <b-badge variant="info" class="command-detail-value">
              {{ cmdData.alarm_levels }}
            </b-badge>
          </div>

          <!-- MOD: Caso Delay como string -->
          <div
            v-else-if="
              typeof item.data.data.cmd_data === 'string' &&
              item.data.data.type === 'Delay'
            "
            class="command-detail-item"
          >
            <span class="command-detail-label">{{
              $t("alarm.timeline.delay_time")
            }}</span>
            <span class="command-detail-value">{{ item.data.data.cmd_data }}s</span>
          </div>

          <!-- MOD: Caso camera_round con visualización dedicada -->
          <div v-else-if="isCameraRound" class="camera-round">
            <div class="command-detail-item">
              <span class="command-detail-label">Loop</span>
              <span class="command-detail-value">{{ boolText(cmdData.loop) }}</span>
            </div>
            <div class="command-detail-item" v-if="cmdData.delay">
              <span class="command-detail-label">{{
                $t("alarm.timeline.delay_time")
              }}</span>
              <span class="command-detail-value">{{ cmdData.delay }}s</span>
            </div>
            <div class="command-detail-item" v-if="cmdData.layout">
              <span class="command-detail-label">Layout</span>
              <span class="command-detail-value">{{ cmdData.layout }}</span>
            </div>
            <div class="command-detail-item" v-if="cmdData.radius">
              <span class="command-detail-label">Radius</span>
              <span class="command-detail-value">{{ cmdData.radius }}</span>
            </div>
            <div class="command-detail-item" v-if="cmdData.location">
              <span class="command-detail-label">Location (lat, lon, r)</span>
              <span class="command-detail-value">{{
                prettyLocation(cmdData.location)
              }}</span>
            </div>

            <div
              class="command-detail-item"
              v-if="Array.isArray(cmdData.users) && cmdData.users.length"
            >
              <span class="command-detail-label">Users</span>
              <div class="command-detail-value chips">
                <span v-for="u in cmdData.users" :key="`user-${u}`" class="chip"
                  >{{ u }}</span
                >
              </div>
            </div>

            <div
              class="command-detail-item"
              v-if="Array.isArray(cmdData.cameras) && cmdData.cameras.length"
            >
              <span class="command-detail-label">Cameras</span>
              <div class="command-detail-value chips">
                <span
                  v-for="cam in cmdData.cameras"
                  :key="`cam-${cam.value}-${cam.label}`"
                  class="chip"
                  :title="`ID: ${cam.value}`"
                >
                  {{ cam.label }} <strong>{{ cam.value }}</strong>
                </span>
              </div>
            </div>
          </div>

          <!-- MOD: Fallback genérico para cmd_data (key/value) -->
          <div v-else class="kv-list">
            <div class="kv-row" v-for="(val, key) in cleanedCmdData" :key="`kv-${key}`">
              <span class="kv-key">{{ key }}</span>
              <span class="kv-val">
                <template v-if="isPrimitive(val)">{{ String(val) }}</template>
                <pre v-else class="json-preview">{{ pretty(val) }}</pre>
              </span>
            </div>
          </div>

          <!-- MOD: User ID (si no se mostró ya en kv-list por limpieza) -->
          <div v-if="typeof cmdData.user_id !== 'undefined'" class="command-detail-item">
            <span class="command-detail-label">{{ $t("alarm.timeline.user_id") }}</span>
            <span class="command-detail-value">{{ cmdData.user_id }}</span>
          </div>
        </div>

        <!-- =================== Dispatcher =================== -->
        <div v-if="item.data.data.dispatcher" class="dispatcher-info mt-1">
          <hr class="my-2" />
          <div class="command-detail-label mb-1">
            {{ $t("alarm.timeline.dispatcher_info") }}:
          </div>

          <div class="command-detail-item">
            <span class="command-detail-label">{{
              $t("alarm.timeline.dispatcher_id")
            }}</span>
            <span class="command-detail-value">{{ dispatcher.id }}</span>
          </div>

          <div v-if="dispatcher.name" class="command-detail-item">
            <span class="command-detail-label">{{
              $t("alarm.timeline.dispatcher_name")
            }}</span>
            <span class="command-detail-value">{{ dispatcher.name }}</span>
          </div>

          <div v-if="dispatcher.source" class="command-detail-item">
            <span class="command-detail-label">{{ $t("alarm.timeline.source") }}</span>
            <span class="command-detail-value">{{ dispatcher.source }}</span>
          </div>

          <div v-if="dispatcher.description" class="command-detail-item">
            <span class="command-detail-label">{{
              $t("alarm.timeline.description")
            }}</span>
            <span class="command-detail-value">{{ dispatcher.description }}</span>
          </div>

          <div v-if="dispatcher.id_user !== undefined" class="command-detail-item">
            <span class="command-detail-label">User ID</span>
            <span class="command-detail-value">{{ dispatcher.id_user }}</span>
          </div>

          <div v-if="dispatcher.id_alarm !== undefined" class="command-detail-item">
            <span class="command-detail-label">Alarm ID</span>
            <span class="command-detail-value">{{ dispatcher.id_alarm }}</span>
          </div>

          <div v-if="dispatcher.id_workflow !== undefined" class="command-detail-item">
            <span class="command-detail-label">{{
              $t("alarm.timeline.workflow_id")
            }}</span>
            <span class="command-detail-value">{{ dispatcher.id_workflow }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- =================== Footer =================== -->
    <div class="command-log-footer">
      <small class="text-muted">
        <template v-if="item.data.user && (item.data.user.email || item.data.user.name)">
          <feather-icon icon="UserIcon" size="14" class="mr-1" />
          {{ $t("alarm.timeline.executed_by") }}:
          {{ item.data.user.email || item.data.user.name }}
        </template>
        <template v-else>
          <feather-icon icon="CpuIcon" size="14" class="mr-1" />
          {{ $t("alarm.timeline.executed_by_system") }}
        </template>
      </small>
    </div>
  </div>
</template>

<script>
/**
 * MOD (resumen):
 * - actionAndTarget (computed) para parsear descripción una sola vez.
 * - isCameraRound + visualización dedicada para cmd_data.camera_round.
 * - Limpieza de cmd_data para fallback genérico key/value.
 * - getActionBadgeVariant ampliado (inference, decision, camera_round, etc.).
 * - Soporte robusto de ai_decision (color, image_url, prompt).
 * - Homologación de clases Bootstrap v4 (mr-*).
 */
import { computed } from "@vue/composition-api";
import { BBadge } from "bootstrap-vue";
import store from "@/store";

export default {
  name: "CommandLogItem",
  components: { BBadge },
  props: {
    item: { type: Object, required: true },
  },
  setup(props) {
    const hasData = computed(
      () => !!props.item?.data && Object.keys(props.item.data).length > 0
    );

    const openImageModal = async (imageUrl) => {
      // Mantiene tu flujo actual de modal de imágenes
      await store.dispatch("image_modal/imageUrl", imageUrl);
    };

    /**
     * MOD: Computed actionAndTarget
     * - Intenta parsear `description`, si no hay, intenta con `cmd_label` o `cmd`.
     */
    const actionAndTarget = computed(() => {
      const d = props.item?.data || {};
      const payload = d.data || {};
      const base =
        d.description || payload.cmd_label || payload.cmd || d.command_name || "";
      return parseCommandDescription(base);
    });

    /**
     * MOD: comodín para acceder a cmd_data, dispatcher
     */
    const cmdData = computed(() =>
      props.item?.data?.data?.cmd_data ? props.item.data.data.cmd_data : {}
    );
    const dispatcher = computed(() => props.item?.data?.data?.dispatcher || {});

    /**
     * MOD: Detección de camera_round por cmd o cmd_label
     */
    const isCameraRound = computed(() => {
      const payload = props.item?.data?.data || {};
      const c = (payload.cmd || "").toLowerCase();
      const l = (payload.cmd_label || "").toLowerCase();
      return c === "camera_round" || l === "camera_round";
    });

    /**
     * MOD: Limpia cmd_data para presentar key/value cuando no cae en casos especiales
     */
    const cleanedCmdData = computed(() => {
      const data = { ...(cmdData.value || {}) };
      // Quitar llaves que ya renderizamos en bloques dedicados
      [
        "ai_decision",
        "alarm_levels",
        "users",
        "cameras",
        "delay",
        "layout",
        "radius",
        "location",
        "loop",
        "prompt", // por si acaso
      ].forEach((k) => {
        if (typeof data[k] !== "undefined") delete data[k];
      });
      return data;
    });

    /**
     * Parser de descripción -> { action, target }
     * Soporta formatos:
     *  - "update:alarm_level"
     *  - "alarm-image-inference" (toma último segmento como action)
     *  - "texto libre" => action = texto, target = "system"
     */
    const parseCommandDescription = (description) => {
      if (!description || typeof description !== "string")
        return { action: "N/A", target: "N/A" };

      let action = "";
      let target = "";

      const desc = description.trim();

      // Casos conocidos
      if (desc === "alarm-image-inference") {
        action = "inference";
        target = "alarm-image";
      } else if (desc === "ai_decision") {
        action = "decision";
        target = "ai-system";
      } else if (desc === "delay") {
        action = "delay";
        target = "execution";
      } else if (desc.includes(":")) {
        // "update:alarm_level"
        const [a, b] = desc.split(":", 2);
        action = (a || "").trim();
        target = (b || "").trim() || "system";
      } else if (desc.includes("-")) {
        // "algo-algo-action"
        const parts = desc
          .split("-")
          .map((p) => p.trim())
          .filter(Boolean);
        if (parts.length > 1) {
          action = parts[parts.length - 1];
          target = parts.slice(0, -1).join("-");
        } else {
          action = parts[0] || "N/A";
          target = "system";
        }
      } else {
        // Texto libre
        action = desc;
        target = "system";
      }

      return {
        action: action || "N/A",
        target: target || "N/A",
      };
    };

    /**
     * Badges variants por tipo de acción
     */
    const getActionBadgeVariant = (action) => {
      const key = (action || "").toString().toLowerCase();
      const variants = {
        update: "info",
        inference: "primary",
        decision: "success",
        delay: "warning",
        execution: "secondary",
        alarm_level: "info",
        "ai-system": "success",
        camera_round: "primary",
        system: "secondary",
        "n/a": "secondary",
      };
      return variants[key] || "secondary";
    };

    /**
     * Color del badge para AI decision (usa el color definido por la respuesta)
     */
    const getAiDecisionColor = (aiDecisionData) => {
      if (!aiDecisionData) return "#6c757d !important";
      // Si trae lista de posibles respuestas con color, priorizar match
      if (Array.isArray(aiDecisionData.possible_answers) && aiDecisionData.ai_decision) {
        const match = aiDecisionData.possible_answers.find(
          (ans) => ans.text === aiDecisionData.ai_decision
        );
        if (match?.color) return `${match.color} !important`;
      }
      // Color directo en la respuesta
      if (aiDecisionData.color) return `${aiDecisionData.color} !important`;
      return "#6c757d !important";
    };

    // Helpers de presentación
    const pretty = (val) => {
      try {
        return JSON.stringify(val, null, 2);
      } catch (e) {
        return String(val);
      }
    };

    const isPrimitive = (val) => {
      return ["string", "number", "boolean"].includes(typeof val) || val === null;
    };

    const boolText = (b) => (b ? "Sí" : "No");

    const prettyLocation = (loc) => {
      if (!loc || typeof loc !== "string") return "";
      const [lat, lon, r] = loc.split(",").map((s) => s.trim());
      if (lat && lon && r) return `${lat}, ${lon} (r=${r})`;
      if (lat && lon) return `${lat}, ${lon}`;
      return loc;
    };

    return {
      hasData,
      openImageModal,
      actionAndTarget,
      cmdData,
      cleanedCmdData,
      dispatcher,
      isCameraRound,
      parseCommandDescription,
      getActionBadgeVariant,
      getAiDecisionColor,
      pretty,
      isPrimitive,
      boolText,
      prettyLocation,
    };
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables/_variables.scss";

/* Mantengo el estilo original y agrego bloques mínimos para chips/kv/json */

.command-log-card {
  background-color: #212529;
  border: 1px solid #495057;
  border-radius: 0.5rem;
  overflow: hidden;
  font-family: "SF Mono", "Fira Code", "Courier New", monospace;
}

.command-log-header {
  display: flex;
  align-items: center;
  padding: 0.5rem .75rem;
  background-color: rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid #495057;
  color: #f8f9fa;
  font-weight: 600;

  .command-name {
    font-size: 0.9rem;
  }
}

.command-log-body {
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.command-detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.command-detail-label {
  font-size: 0.9rem;
  color: #adb5bd;
  margin-right: 1rem;
}

.command-detail-value {
  text-align: end;
}

.command-detail-value.target-value code {
  background-color: #343a40;
  color: #90ee90;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-size: 0.85rem;
}

.action-badge {
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.command-log-footer {
  padding: 0.75rem 1rem;
  background-color: rgba(255, 255, 255, 0.05);
  border-top: 1px solid #495057;
  font-size: 0.8rem;

  .text-muted {
    color: #adb5bd !important;
    display: flex;
    align-items: center;
  }
}

.command-enhanced-info {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;

  hr {
    border-color: #495057;
  }
}

.command-data-section {
  background-color: rgba(255, 255, 255, 0.03);
  padding: 0.75rem .5rem;
  border-radius: 0.25rem;
  border: 1px solid #495057;
}

.ai-decision-data {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.ai-decision-image {
  max-width: 150px;
  max-height: 100px;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
}

.dispatcher-info {
  background-color: rgba(255, 255, 255, 0.02);
  padding: 0.5rem;
  border-radius: 0.25rem;
}

/* MOD: chips para listas (cámaras/usuarios) */
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.chip {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 0.25rem;
  background: #343a40;
  color: #e9ecef;
  border: 1px solid #495057;
  font-size: 0.75rem;
  line-height: 1;
}

/* MOD: tabla key/value fallback */
.kv-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.35rem;

  .kv-row {
    display: grid;
    grid-template-columns: 160px 1fr;
    gap: 0.5rem;
    align-items: start;
  }

  .kv-key {
    color: #adb5bd;
    font-size: 0.85rem;
  }

  .kv-val {
    font-size: 0.85rem;
    word-break: break-word;
  }
}

/* MOD: bloque de código para prompts/textos largos */
.code-block {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  background: #1f1f1f;
  border: 1px solid #495057;
  border-radius: 4px;
  padding: 0.4rem 0.5rem;
  font-size: 0.85rem;
  color: #e9ecef;
}

/* MOD: JSON preview */
.json-preview {
  margin: 0;
  white-space: pre;
  background: #1f1f1f;
  border: 1px solid #495057;
  border-radius: 4px;
  padding: 0.4rem 0.5rem;
  font-size: 0.8rem;
  color: #e9ecef;
}
</style>
